import { Alert, AlertDescription, AlertIcon, Box, Button, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle } from "react-icons/fi";
import {
  BidV2Status,
  type EvalBidDto,
  type SourcingEventV2Dto,
  useGetEvaluationQuery,
} from "../../../../autogen/bff-api";
import { useFinalStageRound } from "../rounds/RoundSelectionContext";

export const useStaleScoreMessage = (): string => {
  const { t } = useTranslation();
  return String(
    t("This score was given before the supplier last delivered, so it may not describe the current tender"),
  );
};

const useStalePriceOverrideMessage = (): string => {
  const { t } = useTranslation();
  return String(
    t("This price was set before the supplier last delivered, so it may not describe the current tender"),
  );
};

const StaleIcon = ({ message }: { message: string }) => (
  <Tooltip label={message} openDelay={200}>
    <Box as="span" display="inline-flex" color="smWarning" role="img" aria-label={message}>
      <Icon as={FiAlertTriangle} boxSize="3.5" />
    </Box>
  </Tooltip>
);

export const StaleScoreIcon = () => <StaleIcon message={useStaleScoreMessage()} />;

export const StalePriceOverrideIcon = () => <StaleIcon message={useStalePriceOverrideMessage()} />;

export const StaleScoreNotice = ({ onKeep }: { onKeep?: () => void }) => {
  const { t } = useTranslation();
  const message = useStaleScoreMessage();
  return (
    <Flex align="center" gap="2" color="smWarning" fontSize="sm" wrap="wrap">
      <Icon as={FiAlertTriangle} boxSize="3.5" flexShrink={0} />
      <Text>{message}</Text>
      {onKeep && (
        <Button size="xs" variant="outline" onClick={onKeep}>
          {t("Keep this score")}
        </Button>
      )}
    </Flex>
  );
};

export const hasDecisionOlderThanDelivery = (bid: EvalBidDto): boolean =>
  bid.cells.some((cell) => cell.scoredBeforeLatestDelivery) || bid.priceOverride?.setBeforeLatestDelivery === true;

export const StaleDecisionsBanner = ({
  bids,
  evaluationCompleted = false,
}: {
  bids: EvalBidDto[];
  evaluationCompleted?: boolean;
}) => {
  const { t } = useTranslation();
  const count = bids.filter((bid) => bid.status !== BidV2Status.Rejected && hasDecisionOlderThanDelivery(bid)).length;
  if (count === 0) return null;
  return (
    <Alert status="warning" rounded="lg">
      <AlertIcon />
      <AlertDescription fontSize="sm">
        {t(
          "{{count}} bids were delivered again after you assessed them. Scores and prices marked with a warning sign describe an earlier version of the bid",
          { count },
        )}
        {" "}
        {evaluationCompleted
          ? t("Reopen the evaluation to check the new version, then keep or change each marked score and price")
          : t("Open the bid, check the new version, then keep or change each marked score and price")}
      </AlertDescription>
    </Alert>
  );
};

export const CompletedEvaluationStaleBanner = ({ event }: { event: SourcingEventV2Dto }) => {
  const finalStageRound = useFinalStageRound();
  const { data } = useGetEvaluationQuery({ eventId: event.id, round: finalStageRound?.id });
  if (!data) return null;
  return <StaleDecisionsBanner bids={data.bids} evaluationCompleted />;
};
